/**
 * rs-product-charm-builder.js
 *
 * Data-driven charm builder for BaubleBar PDP.
 * Async collection loading, lazy DOM rendering, drag-and-drop
 * reordering, MTO validation, multi-item cart payload.
 *
 * Metaobjects: rs_charm_builder → rs_charm_category → rs_charm_subcategory
 * Loaded by rs-product-core.liquid when is_charm_builder is true.
 * Requires rs-charm-builder-core.js (window.RsCharmBuilderBase).
 */

(() => {
  'use strict';

  if (customElements.get('rs-product-charm-builder')) return;
  if (!window.RsCharmBuilderBase) {
    console.error('[CharmBuilder] RsCharmBuilderBase not loaded');
    return;
  }

  customElements.define('rs-product-charm-builder', class extends window.RsCharmBuilderBase {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.sectionId = this.dataset.section;
      this._loadConfig();
      this.slots = new Array(this.charmCount).fill(null);
      this._cache = {};
      this._renderedPanels = new Set();
      this._activeCat = 0;
      this._activeSubcat = {};
      this._pendingCharm = null;
      this._dragSrcIdx = null;
      this._caseHandler = null;

      const boot = () => {
        this._buildUI();
        this._fetchAllCollections();
        this._registerWithCore();
        this._bindVariantChange();
      };

      if (window.RSProduct && typeof window.subscribe === 'function') {
        boot();
      } else {
        document.addEventListener('DOMContentLoaded', boot, { once: true });
      }
    }

    disconnectedCallback() {
      if (this._variantUnsub) { this._variantUnsub(); this._variantUnsub = null; }
      if (window.RSProduct) {
        window.RSProduct.removeGate('charm-builder');
        window.RSProduct.removeItemsBuilder('charm-builder');
      }
      this._ready = false;
    }

    /* ── Hooks ──────────────────────────────────── */

    _beforeUIBuild() {
      this._confirmDialog = this.querySelector('[data-charm-dialog]');
    }

    _afterUIBuild() {
      if (this._confirmDialog) this.appendChild(this._confirmDialog);

      if (this.config.charmsWithDiscount && this.config.discountTiers?.tiers?.length) {
        this._connectTierDropdown();
        this._selectedTier = this.config.discountTiers.tiers[0];
        this._updateTierPriceDisplay();
      }

      this._notifySlots();
    }

    _onSlotChange() {
      this._syncGate();
      if (this.config.charmsWithDiscount) this._updateTierPriceDisplay();
      this._notifySlots();
    }

    // Feeds the mobile sticky CTA's customization row.
    _notifySlots() {
      const charms = (this.slots || []).map(slot => slot ? {
        title: slot.title || '',
        label: slot.itemTitle || slot.title || '',
        imageUrl: slot.imageUrl || ''
      } : null);
      document.dispatchEvent(new CustomEvent('rs:charm-slots-change', {
        detail: { sectionId: this.sectionId, slotLabel: this.config.slotLabel || 'Charm', charms }
      }));
    }

    /* ── Config ─────────────────────────────────── */

    _loadConfig() {
      const el = document.getElementById(`rs-charm-config-${this.sectionId}`);
      this.config = JSON.parse(el.textContent);
      this.charmCount = this.config.charmCount;

      if (this.config.charmsWithDiscount && this.config.discountTiers?.tiers?.length) {
        this.charmCount = this.config.discountTiers.tiers[0].number_of_charms;
        this.config.charmCount = this.charmCount;
      }
    }

    _textInputId(catIdx) {
      return `rs-charm-text-${this.sectionId}-${catIdx}`;
    }

    /* ── Discount Tier Mode ──────────────────────── */

    _connectTierDropdown() {
      this._tierPopout = document.querySelector(`[data-charm-tier-select="${this.sectionId}"]`);
      if (!this._tierPopout) return;

      const input = this._tierPopout.querySelector('[data-popout-input]');
      if (!input) return;

      input.addEventListener('change', () => {
        const qty = parseInt(input.value);
        const index = (this.config.discountTiers?.tiers || [])
          .findIndex(t => t.number_of_charms === qty);
        if (index >= 0) this._selectTier(index);
      });
    }

    _selectTier(index) {
      const tiers = this.config.discountTiers.tiers;
      if (!tiers[index]) return;
      this._selectedTier = tiers[index];
      this._resizeSlots(this._selectedTier.number_of_charms);
      this._updateTierPriceDisplay();
    }

    _updateTierPriceDisplay() {
      const section = this.closest('[data-section-id]')
        || document.querySelector(`[data-section-id="${this.sectionId}"]`);
      const priceEl = section?.querySelector('.price__container .price-item--regular');
      if (!priceEl) return;

      const fmt = (n) => {
        const str = window.theme?.formatMoney
          ? window.theme.formatMoney(n)
          : `$${(n / 100).toFixed(2)}`;
        return str.replace(/\.00$/, '');
      };

      // Find or create the message chip immediately after the [role="status"] price wrapper
      const priceStatusEl = priceEl.closest('[role="status"]');
      let msgEl = priceStatusEl?.nextElementSibling?.dataset.rsCharmPriceMsg
        ? priceStatusEl.nextElementSibling
        : null;
      if (!msgEl && priceStatusEl) {
        msgEl = document.createElement('p');
        msgEl.dataset.rsCharmPriceMsg = '1';
        msgEl.className = 'rs-charm-price-msg';
        msgEl.hidden = true;
        priceStatusEl.after(msgEl);
      }

      const filled = (this.slots || []).filter(s => s !== null).length;
      const needed = (this.charmCount || 0) - filled;

      // Remove any previously injected sale structure and price--on-sale state
      const container    = priceEl.closest('.price__container');
      const priceWrapper = priceEl.closest('.price');
      container?.querySelector('[data-rs-charm-sale]')?.remove();
      priceWrapper?.classList.remove('price--on-sale', 'product__price--sale');

      if (needed > 0) {
        if (msgEl) {
          msgEl.hidden = false;
          const label = (this.config.slotLabel || 'Charm').toLowerCase();
          msgEl.textContent = filled === 0
            ? `Add your ${label}s to see updated price`
            : `Add ${needed} more ${label}${needed === 1 ? '' : 's'} to see updated price`;
        }
        priceEl.textContent = fmt(this.config.productBasePrice || 0);
        return;
      }

      // All slots filled — show discounted price and set label
      if (msgEl) {
        msgEl.textContent = `${this.config.slotLabel || 'Charm'} Set Price`;
        msgEl.hidden = false;
      }

      const _showBundle = () => {
        const charmTotal      = (this.slots || []).reduce((sum, s) => sum + (s?.price || 0), 0);
        const totalCents      = (this.config.productBasePrice || 0) + charmTotal;
        const discountCents   = Math.round((this._selectedTier?.discount_amount || 0) * 100);
        const discountedCents = Math.max(0, totalCents - discountCents);

        priceEl.textContent = fmt(discountedCents > 0 ? discountedCents : totalCents);
      };

      const missingPrice = (this.slots || []).filter(s => s?.variantId && !s.price);
      if (missingPrice.length > 0) {
        this._fetchVariantPrices(missingPrice.map(s => s.variantId)).then(prices => {
          this.slots.forEach(slot => {
            if (slot?.variantId && prices[slot.variantId] != null) {
              slot.price = prices[slot.variantId];
            }
          });
          _showBundle();
        });
      } else {
        _showBundle();
      }
    }

    async _fetchVariantPrices(variantIds) {
      const query = `
        query GetVariantPrices($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on ProductVariant {
              id
              price { amount }
            }
          }
        }`;
      const gids = variantIds.map(id => `gid://shopify/ProductVariant/${id}`);
      try {
        const data = await this._storefrontQuery(query, { ids: gids });
        const prices = {};
        (data?.nodes || []).forEach(node => {
          if (node?.id && node?.price) {
            prices[this._stripGid(node.id)] = Math.round(parseFloat(node.price.amount) * 100);
          }
        });
        return prices;
      } catch (e) {
        return {};
      }
    }

    /* ── RSProduct Integration ─────────────────── */

    _registerWithCore() {
      if (!window.RSProduct) return;

      window.RSProduct.registerGate('charm-builder', {
        disabled: true,
        text: window.theme?.strings?.selectIcons || 'Please Select Icons',
        priority: 10
      });

      window.RSProduct.registerItemsBuilder('charm-builder', () => this._buildCartItems());
    }

    _syncGate() {
      if (!window.RSProduct?.hasGate('charm-builder')) return;
      const blankEnabled = this.config.enableBlankProduct && this.config.blankVariantId;
      const enabled = blankEnabled
        ? Array.isArray(this.slots) && this.slots.some(s => s)
        : Array.isArray(this.slots) && this.slots.every(s => s);
      window.RSProduct.updateGate('charm-builder', {
        disabled: !enabled,
        text: enabled
          ? (window.theme?.strings?.addToCart || 'Add to Cart')
          : (window.theme?.strings?.selectIcons || 'Please Select Icons')
      });
    }

    async _buildCartItems() {
      const blankEnabled = this.config.enableBlankProduct && this.config.blankVariantId;
      const blankIndices = [];
      this.slots.forEach((s, i) => { if (!s) blankIndices.push(i + 1); });

      if (blankIndices.length === this.charmCount) {
        this._showBlankAlert('Please select at least one charm before adding to cart.');
        return null;
      }

      if (blankIndices.length > 0) {
        if (!blankEnabled) return null;
        const confirmed = await this._confirmBlankSlots(blankIndices);
        if (!confirmed) return null;
      }

      const form = document.getElementById(this.config.productFormId);
      const variantInput = form?.querySelector('[name=id]');
      if (!variantInput) return null;

      const qtyInput = form.elements['quantity'];
      const qty = Math.max(1, parseInt(qtyInput?.value) || 1);

      const ref = crypto.randomUUID?.() ?? (Date.now().toString(36) + Math.random().toString(36).slice(2, 7));

      const formProps = {};
      Array.from(form.elements).forEach(inp => {
        if (inp.hasAttribute('data-rs-prop') && !inp.disabled && inp.value) {
          formProps[inp.name.replace('properties[', '').replace(']', '')] = inp.value;
        }
      });

      const children = [];

      this.slots.forEach((charm, index) => {
        const props = {
          '_ref': ref,
          '_componentIndex': index,
          '_builderChild': 'true'
        };
        if (charm && charm.variantId) {
          if (charm.customization) props['Customization'] = charm.customization;
          children.push({ id: parseInt(charm.variantId), quantity: qty, properties: props });
        } else if (blankEnabled) {
          children.push({ id: parseInt(this.config.blankVariantId), quantity: qty, properties: props });
        }
      });

      const parentProperties = {
        ...formProps,
        '_ref': ref,
        '_componentIndex': children.length,
        '_isParentProduct': 'true',
        '_builder': 'true'
      };

      // Discount tier properties
      if (this.config.charmsWithDiscount && this._selectedTier) {
        // Ensure slot prices are available for _charmTotal
        const missingPrice = this.slots.filter(s => s?.variantId && !s.price);
        if (missingPrice.length > 0) {
          const prices = await this._fetchVariantPrices(missingPrice.map(s => s.variantId));
          this.slots.forEach(slot => {
            if (slot?.variantId && prices[slot.variantId] != null) {
              slot.price = prices[slot.variantId];
            }
          });
        }

        const charmTotal = (this.slots || []).reduce((sum, s) => sum + (s?.price || 0), 0);
        const bundleTotal = (this.config.productBasePrice || 0) + charmTotal;

        parentProperties['_charmWithDiscount']    = 'true';
        parentProperties['_numberOfCharms']       = String(this._selectedTier.number_of_charms);
        parentProperties['_charmDiscountAmount']  = String(this._selectedTier.discount_amount);
        parentProperties['_charmTotal']           = String(bundleTotal); // cents
      }

      const parent = {
        id: parseInt(variantInput.value),
        quantity: qty,
        properties: parentProperties
      };

      return [...children, parent].reverse();
    }

    /* ── Variant Change ────────────────────────── */

    _bindVariantChange() {
      // Discount-tier products have no meaningful variant-driven slot count
      if (this.config.charmsWithDiscount) return;

      if (typeof window.subscribe !== 'function') return;

      this._variantUnsub = window.subscribe(
        window.theme.PUB_SUB_EVENTS.variantChange,
        (event) => {
          if (!event?.data) return;
          if (event.data.sectionId && event.data.sectionId !== this.sectionId) return;

          const { variant } = event.data;
          if (!variant) return;

          const counts = this.config.variantCharmCounts || {};
          const newCount = counts[variant.id] != null ? counts[variant.id] : this.charmCount;
          if (newCount !== this.charmCount) {
            this._resizeSlots(newCount);
            this._notifySlots();
          }

          this._syncGate();
        }
      );
    }
  });
})();
